import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Download, BarChart3, Users, Clock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['hsl(243, 75%, 59%)', 'hsl(160, 84%, 39%)', 'hsl(38, 92%, 50%)', 'hsl(270, 70%, 60%)', 'hsl(0, 84%, 60%)'];

export default function AdminReports() {
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const generateReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reports/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: format(dateRange.start, 'yyyy-MM-dd'),
          end_date: format(dateRange.end, 'yyyy-MM-dd')
        }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  const setQuickRange = (range) => {
    const today = new Date();
    switch (range) {
      case 'thisMonth':
        setDateRange({
          start: startOfMonth(today),
          end: endOfMonth(today)
        });
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        setDateRange({
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth)
        });
        break;
      case 'last3Months':
        setDateRange({
          start: startOfMonth(subMonths(today, 2)),
          end: endOfMonth(today)
        });
        break;
      default:
        break;
    }
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '0h';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const exportToCSV = () => {
    if (!report) return;

    const headers = ['Employee', 'Email', 'Total Hours', 'Total Entries', 'Approved Entries'];
    const rows = report.employees.map(emp => [
      emp.name,
      emp.email,
      emp.total_hours.toFixed(2),
      emp.total_entries,
      emp.approved_entries
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `timesheet_report_${format(dateRange.start, 'yyyy-MM-dd')}_${format(dateRange.end, 'yyyy-MM-dd')}.csv`;
    link.click();
    
    toast.success('Report exported successfully');
  };

  const chartData = report?.employees?.map(emp => ({
    name: emp.name.split(' ')[0],
    hours: emp.total_hours
  })) || [];

  const pieData = report?.employees?.filter(emp => emp.total_hours > 0).map(emp => ({
    name: emp.name,
    value: emp.total_hours
  })) || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold">Reports</h1>
            <p className="text-muted-foreground">Generate and export timesheet reports</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Quick Ranges */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setQuickRange('thisMonth')}
                data-testid="this-month-report-btn"
              >
                This Month
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setQuickRange('lastMonth')}
                data-testid="last-month-report-btn"
              >
                Last Month
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setQuickRange('last3Months')}
                data-testid="last-3-months-btn"
              >
                Last 3 Months
              </Button>

              {/* Custom Date Range */}
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="report-date-picker">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={{ from: dateRange.start, to: dateRange.end }}
                    onSelect={(range) => {
                      if (range?.from && range?.to) {
                        setDateRange({ start: range.from, end: range.to });
                        setCalendarOpen(false);
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>

              <div className="flex items-center gap-2 ml-auto">
                <Button onClick={generateReport} disabled={loading} data-testid="generate-report-btn">
                  {loading ? (
                    <div className="h-4 w-4 spinner border-primary-foreground mr-2" />
                  ) : (
                    <BarChart3 className="h-4 w-4 mr-2" />
                  )}
                  Generate Report
                </Button>
                {report && (
                  <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Results */}
        {report && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card data-testid="report-total-entries">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Total Entries
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{report.total_entries}</div>
                </CardContent>
              </Card>
              <Card data-testid="report-total-hours">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Total Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatHours(report.total_hours)}</div>
                </CardContent>
              </Card>
              <Card data-testid="report-employees">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Employees
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{report.employees.length}</div>
                </CardContent>
              </Card>
              <Card data-testid="report-avg-hours">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Avg Hours/Employee</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {formatHours(report.employees.length > 0 ? report.total_hours / report.employees.length : 0)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bar Chart */}
              <Card data-testid="hours-bar-chart">
                <CardHeader>
                  <CardTitle>Hours by Employee</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="name" className="text-xs" />
                        <YAxis className="text-xs" />
                        <Tooltip 
                          formatter={(value) => [`${value.toFixed(1)}h`, 'Hours']}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Pie Chart */}
              <Card data-testid="hours-pie-chart">
                <CardHeader>
                  <CardTitle>Hours Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {pieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => [`${value.toFixed(1)}h`, 'Hours']}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No data to display
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Table */}
            <Card>
              <CardHeader>
                <CardTitle>Employee Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Total Hours</TableHead>
                        <TableHead>Total Entries</TableHead>
                        <TableHead>Approved</TableHead>
                        <TableHead>Pending</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.employees.map((emp, index) => (
                        <TableRow key={index} data-testid={`report-employee-row-${index}`}>
                          <TableCell className="font-medium">{emp.name}</TableCell>
                          <TableCell className="text-muted-foreground">{emp.email}</TableCell>
                          <TableCell className="font-medium">{formatHours(emp.total_hours)}</TableCell>
                          <TableCell>{emp.total_entries}</TableCell>
                          <TableCell className="text-emerald-600">{emp.approved_entries}</TableCell>
                          <TableCell className="text-amber-600">
                            {emp.total_entries - emp.approved_entries}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Empty State */}
        {!report && !loading && (
          <Card>
            <CardContent className="py-16">
              <div className="text-center text-muted-foreground">
                <BarChart3 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Report Generated</h3>
                <p className="mb-4">Select a date range and click "Generate Report" to view analytics</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
